/**
 * TeamsClient - HTTP wrapper around `/api/admin/teams*`.
 *
 * Mirrors UserDirectoryClient's shape (plain object, static methods) but goes
 * through the shared `adminFetch` helper since these are admin-gated
 * (`u.view` / `u.edit`) mutations, not the read-only public directory.
 */
import type {
  AdminTeamResponse,
  AdminTeamsResponse,
  TeamId,
  TeamWithMembers,
  UserId,
} from "@taskin-code/contracts";

import { adminFetch } from "../lib/adminFetch";

export const TeamsClient = {
  async list(): Promise<ReadonlyArray<TeamWithMembers>> {
    const result = await adminFetch<AdminTeamsResponse>("/api/admin/teams");
    return result.teams;
  },

  async create(name: string): Promise<TeamWithMembers> {
    const result = await adminFetch<AdminTeamResponse>("/api/admin/teams", {
      method: "POST",
      body: JSON.stringify({ name }),
    });
    return result.team;
  },

  async rename(teamId: TeamId, name: string): Promise<TeamWithMembers> {
    const result = await adminFetch<AdminTeamResponse>(`/api/admin/teams/${teamId}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    });
    return result.team;
  },

  async remove(teamId: TeamId): Promise<void> {
    await adminFetch<{ ok: true }>(`/api/admin/teams/${teamId}`, { method: "DELETE" });
  },

  async addMember(teamId: TeamId, userId: UserId): Promise<TeamWithMembers> {
    const result = await adminFetch<AdminTeamResponse>(
      `/api/admin/teams/${teamId}/members/${userId}`,
      { method: "PUT" },
    );
    return result.team;
  },

  async removeMember(teamId: TeamId, userId: UserId): Promise<TeamWithMembers> {
    const result = await adminFetch<AdminTeamResponse>(
      `/api/admin/teams/${teamId}/members/${userId}`,
      { method: "DELETE" },
    );
    return result.team;
  },
};
